"use client";
import { useHydrated } from "@/hooks/useHydrated";
import { usePublicQuery } from "@/lib/convex/hooks/useAuthedQuery";
import { readBrandKitCookie, writeBrandKitCookie } from "@/lib/theme/brand-kit-cookie";
import { DISPUTEWELL_PLATFORM_BRAND, FLUID_MARK_KIT_PROPERTIES } from "@/lib/theme/branding";
import { api } from "@convex/_generated/api";
import { BRAND_IMAGE_SLOTS } from "@convex/tables/brandKits";
import { type BrandImages, NO_BRAND_IMAGES, type WornBrandKit } from "@convex/validators/brandKits";
import { createContext, memo, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

type WearBrandKit = (kit: WornBrandKit | null) => void;

const BrandImagesContext = createContext<{ kit: WornBrandKit | null; wear: WearBrandKit }>({ kit: null, wear: () => {} });

/** Points the tab icon and every Fluid mark at a kit's images in this tab: both are set outside React,
 *  the icon in the page's metadata and the marks in the hooks the root layout fills (registry.css). */
function showKitInDocument(images: BrandImages): void {
    const iconHref = images.disputewellIcon?.url ?? DISPUTEWELL_PLATFORM_BRAND.iconUrl;
    for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="apple-touch-icon"]')) {
        link.setAttribute("href", iconHref);
    }
    for (const slot of BRAND_IMAGE_SLOTS) {
        const property = FLUID_MARK_KIT_PROPERTIES[slot];
        if (property) {
            // "initial" empties the hook over the root layout's first-paint value, and the mark falls back
            // to the next image in line.
            const image = images[slot];
            document.documentElement.style.setProperty(property, image ? `url("${image.url}")` : "initial");
        }
    }
}

/**
 * Carries the brand kit this browser wears (User settings → Appearance → Brand images) to the
 * platform's logo (DisputeWellLogo) and the setting. Fluid's mark needs no context: every mark reads
 * the kit's image from a hook the root layout sets. The root layout reads the kit on the server, so it
 * is in the first paint; null is the Default kit. A static page (the marketing site, help, privacy,
 * terms) is rendered without the request's cookies, so there the browser reads the kit its cookie
 * names, just after the first paint.
 *
 * Wearing another kit writes the browser's cookie and changes this tab at once, without a refresh: a
 * refresh remounts the CRM shell and would close the settings sheet. The next server read (a reload, a
 * refresh) is the truth again.
 */
export const BrandImagesProvider = memo(function BrandImagesProvider(
    { kit: fromServer, children }: { kit: WornBrandKit | null; children: ReactNode }
) {
    const [local, setLocal] = useState<{ base: WornBrandKit | null; kit: WornBrandKit | null } | null>(null);
    const hydrated = useHydrated();
    const cookieKitId = hydrated && fromServer === null ? readBrandKitCookie() : null;
    const fromBrowser = usePublicQuery(api.brandKits.worn, cookieKitId ? { kitId: cookieKitId } : "skip") ?? null;
    const kit = local && local.base === fromServer ? local.kit : fromServer ?? fromBrowser;

    // The server drew the tab icon and the Fluid mark hooks without the kit the browser read: point them at it.
    useEffect(() => {
        if (fromBrowser && local === null) {
            showKitInDocument(fromBrowser.images);
        }
    }, [fromBrowser, local]);

    const wear = useCallback<WearBrandKit>((next) => {
        writeBrandKitCookie(next?.kitId ?? null);
        setLocal({ base: fromServer, kit: next });
        showKitInDocument(next?.images ?? NO_BRAND_IMAGES);
    }, [fromServer]);

    const value = useMemo(() => ({ kit, wear }), [kit, wear]);
    return <BrandImagesContext.Provider value={value}>{children}</BrandImagesContext.Provider>;
});

/** The kit this browser wears; null is the Default kit. */
export function useWornBrandKit(): WornBrandKit | null {
    return useContext(BrandImagesContext).kit;
}

/** The worn kit's images: every slot empty for the Default kit. */
export function useBrandImages(): BrandImages {
    return useContext(BrandImagesContext).kit?.images ?? NO_BRAND_IMAGES;
}

export function useWearBrandKit(): WearBrandKit {
    return useContext(BrandImagesContext).wear;
}
